import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useParams, Link } from 'react-router-dom'
import styles from './RegionsDirectory.scss'
import { Card } from 'common/styleguide/Card'
import { SectionBox } from 'common/styleguide/SectionBox'
import { SearchableCard } from 'common/SearchableCard'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/StandardLayout/TwoCol'
import { StandardLayout } from 'common/layouts/StandardLayout'
import { withCollapseState } from 'common/layouts/StandardLayout/StandardLayoutContexts'

import { Loadable } from 'common/Loadable'
import { Hero } from 'common/styleguide/Hero'
import { DirectoryListItem, SideColRegionListItem } from '../RegionListItems'
import { ZoneCard } from '../ZoneCard'
import { useFormatMessage, featureNames } from 'lib/localization'
import { defineMessages } from 'react-intl'
import { regionSelectors, regionActions } from 'data/resources/regions'
import { promiseMapAll } from 'lib/utils'
import cx from 'classnames'

const POPULAR_GLOBAL_REGIONS = [
  {
    'name': 'United States',
    'location': '/regions/north_america/us'
  },
  {
    'name': 'Canada',
    'location': '/regions/north_america/ca'
  },
  {
    'name': 'Great Britain',
    'location': '/regions/europe/gb'
  },
  {
    'name': 'Italy',
    'location': '/regions/europe/it'
  },
  {
    'name': 'Spain',
    'location': '/regions/europe/es'
  }
]

const POPULAR_US_REGIONS = [
  {
    'name': 'Portland, OR',
    'location': '/regions/north_america/us/1-portland-oregon-usa'
  },
  {
    'name': 'Seattle, WA',
    'location': '/regions/north_america/us/3-seattle-washington-usa'
  },
  {
    'name': 'Los Angeles, CA',
    'location': '/regions/north_america/us/38-los-angeles-california-usa'
  },
  {
    'name': 'New York, NY',
    'location': '/regions/north_america/us/5-new-york-city-new-york-usa'
  },
  {
    'name': 'Boston, MA',
    'location': '/regions/north_america/us/6-boston-massachusetts-usa'
  }
]

const messages = defineMessages({
  loadingText: {
    id: 'regions.loadingText',
    defaultMessage: 'Loading regions...'
  },
  introText: {
    id: 'regions.introText',
    defaultMessage: 'Curated cycling guides with the best bike routes in your area.'
  },
  mainColTitle: {
    id: 'regions.mainColTitle',
    defaultMessage: 'Find a region'
  },
  popularGlobalViewAll: {
    id: 'regions.globalViewAll',
    defaultMessage: 'View Global Regions'
  },
  popularUsViewAll: {
    id: 'regions.usViewAll',
    defaultMessage: 'View US Regions'
  },
  popularGlobalTitle: {
    id: 'regions.popularGlobalTitle',
    defaultMessage: 'Popular Global Regions'
  },
  popularUsTitle: {
    id: 'regions.popularUsTitle',
    defaultMessage: 'Popular US Regions'
  },
  routesLabel: {
    id: 'regions.routesLabel',
    defaultMessage: 'routes'
  },
  searchPlaceholder: {
    id: 'regions.searchPlaceholder',
    defaultMessage: 'Search for a region'
  },
  searchNoResults: {
    id: 'regions.searchNoResults',
    defaultMessage: 'We couldn\'t find any regions that matched your search'
  }
})

const _RegionsList = ({ allRegions, zone, countryCode, regionsFromServer, regions, keywords, onKeywordChange, fm, collapseState }) => {
  const countryIds = allRegions[zone][countryCode]
  const selectedRegions = regionsFromServer || regions(countryIds)
  const filteredRegions = selectedRegions.filter(region => keywords === '' || region.name.toLowerCase().includes(keywords.toLowerCase()))
  return (
    <Card noPadding className={cx(styles.regionsListCard, collapseState && styles.collapsed)}>
      <SearchableCard
        keywords={keywords}
        onKeywordChange={onKeywordChange}
        placeholder={fm(messages.searchPlaceholder)}
        resultsCount={filteredRegions.length}
        resultsLength={filteredRegions.length}>
        {filteredRegions.length > 0 ? (
          <div>
            {filteredRegions.sort((a, b) => a.name.localeCompare(b.name)).map(region => <DirectoryListItem region={region} fm={fm} labelString={messages.routesLabel} />)}
          </div>
        ) : (
          <div className={styles.searchNoResults}>
            <span>{fm(messages.searchNoResults)}</span>
          </div>
        )}
      </SearchableCard>
    </Card>
  )
}

const RegionsList = withCollapseState(_RegionsList)

const PopularCard = ({ countryCode, fm }) => {
  const title = countryCode ? fm(messages.popularGlobalTitle) : fm(messages.popularUsTitle)
  const popularRegions = countryCode ? POPULAR_GLOBAL_REGIONS : POPULAR_US_REGIONS
  const viewMore = countryCode ? (
    <Link data-card-link to='/regions'>
      {fm(messages.popularGlobalViewAll)}
    </Link>
  ) : (
    <Link data-card-link to='/regions/north_america/us'>
      {fm(messages.popularUsViewAll)}
    </Link>
  )
  return (
    <SectionBox title={title} className={styles.popularCard}>
      {popularRegions.map((region, index) => <SideColRegionListItem
        key={index}
        name={region.name}
        to={region.location}
        idx={index}
      />)}
      {viewMore}
    </SectionBox>
  )
}

const _ZoneCards = ({ allRegions, namesI18n, collapseState }) => {
  // calculate card height for all cards based on longest country list
  let maxCountries = 0
  for (let zone in allRegions) {
    for (let country in zone) {
      if (country.length > maxCountries) {
        maxCountries = country.length
      }
    }
  }
  const cardHeight = `${(maxCountries / 2) * 26}px`
  return (
    <div className={styles.zoneCards}>
      {Object.keys(allRegions).map(zone => <ZoneCard
        key={zone}
        zone={zone}
        countries={Object.keys(allRegions[zone])}
        namesI18n={namesI18n}
        isMobile={collapseState}
        cardHeight={cardHeight}
      />)}
    </div>
  )
}

const ZoneCards = withCollapseState(_ZoneCards)

export const _RegionsDirectory = ({ countryCode, ...rest }) => {
  const [keywords, setKeywords] = useState('')
  const fm = useFormatMessage()

  const commonProps = { countryCode, keywords, fm, ...rest }

  const onKeywordChange = (e) => {
    const input = e.target.value
    e.preventDefault()
    setKeywords(input)
  }

  return (
    <ColWrap className={styles.colWrap}>
      <MainCol className={styles.mainCol}>
        <h2 className={styles.mainColTitle} data-heading-alt>{fm(messages.mainColTitle)}</h2>
        {countryCode
          ? <RegionsList
            onKeywordChange={onKeywordChange}
            {...commonProps} />
          : <ZoneCards
            {...commonProps} />
        }
      </MainCol>
      <SecondaryCol>
        <PopularCard
          {...commonProps} />
      </SecondaryCol>
    </ColWrap>
  )
}

const loadInitialResources = ({ loadRegions, zone, countryCode }) => {
  return promiseMapAll({
    regions: loadRegions({ zone, countryCode })
  })
}

const LoadableRegionsDirectory = props => {
  const fm = useFormatMessage()
  const { countryCode, zone } = useParams()
  return <Loadable
    {...props}
    countryCode={countryCode}
    zone={zone}
    resourceId={`${zone}-${countryCode}`}
    component={_RegionsDirectory}
    loadingText={fm(messages.loadingText)}
    resourceLoaded={() => loadInitialResources({ zone, countryCode, ...props })}
  />
}

// this is constructed this way so that we keep the layout and hero
// when we nav between countries/zones. otherwise the loadable spinner
// takes over the entire screen
const RegionsDirectoryLayout = props => {
  const fm = useFormatMessage()
  return <StandardLayout greyBg withCollapseProvider>
    <Hero
      small
      short
      paddingVertical='140px'
      marginBottom='0px'
      scrimOpacity={[0.2, 0.7]}
      img={'/images/regions/hero.png'}
      img2x={'/images/regions/hero@2x.png'}
      focalPoint='center 50%'
      label={fm(featureNames.regions)}
    >
      {fm(messages.introText)}
    </Hero>
    <LoadableRegionsDirectory {...props} />
  </StandardLayout>
}

const mapStateToProps = (state, ownProps) => {
  return {
    regions: ids => regionSelectors.findByIds(state, ids),
    allRegions: regionSelectors.allRegions(state),
    namesI18n: regionSelectors.namesI18n(state)
  }
}

const mapDispatchToProps = dispatch => {
  return {
    loadRegions: params => dispatch(regionActions.list(params))
  }
}

export const RegionsDirectory = connect(
  mapStateToProps,
  mapDispatchToProps
)(RegionsDirectoryLayout)
